import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DialogTitle } from "@radix-ui/react-dialog";
import { Loader2 } from "lucide-react";
import { useState } from "react";

export function DeleteConfirmationDialog({
    open,
    onOpenChange,
    onConfirm,
    isPending,
    itemName,
    itemType = "Portal Forms",
    requireAuthCode = false,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: (authCode?: string) => void;
    isPending: boolean;
    itemName?: any;
    itemType?: "Portal Forms" | "RuleFunction";
    requireAuthCode?: boolean;

}) {
    const [step, setStep] = useState<"confirm" | "auth">("confirm");
    const [authCode, setAuthCode] = useState("");

    const handleNext = () => {
        if (requireAuthCode) {
            setStep("auth");
        } else {
            onConfirm();
        }
    };

    const handleAuthConfirm = () => {
        onConfirm(authCode);
        setAuthCode("")
    };

    const handleClose = () => {
        setStep("confirm");
        setAuthCode("");
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                    <DialogTitle>
                        {step === "confirm" ? "Confirm Deletion" : "Authorization Required"}
                    </DialogTitle>
                </DialogHeader>

                {step === "confirm" ? (
                    <div className="space-y-4">
                        <p className="text-gray-600">
                            Are you sure you want to delete{" "}
                            {itemName?.Name ? (
                                <span className="font-semibold">"{itemName?.FormName}"</span>
                            ) : (
                                `this ${itemType}`
                            )}
                            ?
                        </p>
                        <div className="flex justify-end space-x-2">
                            <Button
                                type="button"
                                variant="destructive"
                                disabled={isPending}
                                onClick={handleNext}
                            >
                                {isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    "Delete"
                                )}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <p className="text-gray-600">
                            Please enter your authorization code to confirm deletion of{" "}
                            {itemName?.Name ? (
                                <span className="font-semibold">"{itemName?.FormName}"</span>
                            ) : (
                                `this ${itemType}`
                            )}
                            .
                        </p>
                        <Input
                            type="text"
                            placeholder="Enter authorization code"
                            value={authCode}
                            onChange={(e) => setAuthCode(e.target.value)}
                        />
                        <div className="flex justify-end space-x-2">
                            <Button
                                variant="destructive"
                                disabled={!authCode || isPending}
                                onClick={handleAuthConfirm}
                            >
                                {isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    "Confirm Delete"
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
